'use client';

import { FC, useEffect, useRef, useState } from 'react';

import { PinnedSlideshowDto } from '@/components/PinnedSlideshowSection/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { isArrayNotEmpty } from '@/helpers/array';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import { getMedia } from '@/lib/utils';

import css from './style.module.scss';

interface Props {
	data: PinnedSlideshowDto;
	className?: string;
}

const PinnedSlideshowSection: FC<Props> = ({ data, className }) => {
	const { images, title, items: itemsList } = data;

	const device = useDevice();
	const triggerRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const slidesRef = useRef<HTMLDivElement[]>([]);
	const [loadedIndexes, setLoadedIndexes] = useState<number[]>([]);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const initializedSlidesRef = useRef(new Set<number>());

	// Lazy load background images
	useEffect(() => {
		if (!images.length) return;

		const observer = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const index = Number(entry.target.getAttribute('data-index'));
						if (!loadedIndexes.includes(index)) setLoadedIndexes(prev => [...prev, index]);

						observer.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: '400px 0px' }
		);

		slidesRef.current.forEach(slide => observer.observe(slide));
		return () => observer.disconnect();
	}, [images, loadedIndexes]);

	const currentIndexRef = useRef(0);

	useEffect(() => {
		loadedIndexes.forEach(i => {
			if (initializedSlidesRef.current.has(i)) return;
			initializedSlidesRef.current.add(i);

			if (i !== currentIndexRef.current) return;
			const slide = slidesRef.current[i];
			if (slide)
				gsap.fromTo(slide, { opacity: 0 }, { opacity: 1, duration: 0.6, ease: 'power2.out' });
		});
	}, [loadedIndexes]);

	useEffect(() => {
		if (!images.length) return;

		const interval = setInterval(() => {
			const prevIndex = currentIndexRef.current;
			currentIndexRef.current = (currentIndexRef.current + 1) % images.length;
			const nextIndex = currentIndexRef.current;

			const prevSlide = slidesRef.current[prevIndex];
			const nextSlide = slidesRef.current[nextIndex];

			if (prevSlide) {
				gsap.set(prevSlide, { zIndex: 0 });
				gsap.to(prevSlide, { opacity: 0, duration: 2, ease: 'power2.inOut' });
			}
			if (nextSlide) {
				gsap.set(nextSlide, { zIndex: 1 });
				gsap.to(nextSlide, { opacity: 1, duration: 2, ease: 'power2.inOut' });
			}
		}, 3000);

		return () => clearInterval(interval);
	}, [images]);

	useEffect(() => {
		const wrapper = wrapperRef.current;
		const listItems = gsap.utils.toArray<HTMLDivElement>(`.${css.listItem}`);
		const listWrapper = wrapper?.querySelector(`.${css.listWrapper}`);
		// const title = wrapper?.querySelector(`.${css.title}`);
		const slides = gsap.utils.toArray<HTMLImageElement>(`.${css.slide}`);

		if (!wrapper || !listItems || !listWrapper) return;

		const ctx = gsap.context(() => {
			const matchMedia = gsap.matchMedia();

			matchMedia.add(
				{
					isDesktop: `(min-width: 992px)`,
					isMobile: `(max-width: 991px)`
				},
				context => {
					const { isMobile } = context.conditions as gsap.Conditions;

					if (isMobile) return;

					gsap.to([slides], {
						ease: 'none',
						backgroundPositionY: '100%',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top',
							end: 'bottom+=300% bottom+=25%',
							scrub: true,
							markers: false
						}
					});

					gsap.to(wrapper, {
						scrollTrigger: {
							id: 'main-trigger',
							trigger: wrapper,
							start: 'top',
							end: 'bottom+=300% bottom+=25%',
							pin: true,
							pinSpacing: true
						}
					});

					// if (title) {
					// 	gsap.fromTo(
					// 		title,
					// 		{ y: 80, opacity: 0 },
					// 		{
					// 			y: 0,
					// 			opacity: 1,
					// 			duration: 1.4,
					// 			ease: 'power3.out',
					// 			scrollTrigger: {
					// 				trigger: title,
					// 				start: 'top 90%',
					// 				toggleActions: 'play none none reverse'
					// 			}
					// 		}
					// 	);

					// 	gsap.to(title, {
					// 		yPercent: -50,
					// 		ease: 'none',
					// 		scrollTrigger: {
					// 			trigger: wrapper,
					// 			start: '25% bottom',
					// 			end: '10% top',
					// 			scrub: true
					// 		}
					// 	});
					// }

					gsap.to(listWrapper, {
						yPercent: -30,
						y: window.innerHeight * 0.8 - listWrapper.clientHeight,
						force3D: !isMobile,
						willChange: 'transform',
						ease: 'none',
						scrollTrigger: {
							id: 'wrap-to-top',
							trigger: triggerRef.current,
							start: '18% top',
							end: 'bottom',
							markers: false,
							scrub: true
						}
					});

					gsap.set(listItems, {
						opacity: 0
					});

					gsap.from(listItems, {
						keyframes: [
							{ opacity: 0, rotationY: -90 },
							{ opacity: 1, rotationY: 0 },
							{ opacity: 0, rotationY: 90 }
						],
						force3D: !isMobile,
						willChange: 'transform, opacity',
						stagger: 0.5,
						ease: 'power2.out',
						opacity: 0,
						scrollTrigger: {
							id: 'wrap-to-top',
							trigger: triggerRef.current,
							start: '9% top',
							end: 'bottom',
							markers: false,
							scrub: 1
						}
					});
				}
			);
		}, wrapperRef);

		return () => ctx.revert();
	}, [device]);

	return (
		<section ref={triggerRef} className={className}>
			<div ref={wrapperRef} className={css.wrapper}>
				<div className={css.slideshow}>
					{images.map((item, index) => (
						<div
							key={index}
							ref={el => {
								if (el) slidesRef.current[index] = el;
							}}
							data-index={index}
							className={css.slide}
							style={{
								backgroundImage: loadedIndexes.includes(index)
									? `url(${item.image?.url ?? getMedia(item.image)?.url})`
									: undefined
							}}
						/>
					))}
				</div>

				{title ? (
					<h2 ref={titleRef} className={css.title}>
						{title}
					</h2>
				) : null}

				<div className={css.listWrapper}>
					<ul className={css.itemsList}>
						{isArrayNotEmpty(itemsList) &&
							itemsList.map(({ id, title, description }, idx) => (
								<li key={id} className={css.listItem}>
									{device === 'mobile' && <span className={css.index}>0{idx + 1}</span>}

									<Typography uppercase className={css.itemTitle} variant="h2" tag="h3">
										<StrapiRichContent content={title} />
									</Typography>

									<Typography className={css.itemDescription} variant="body-l" tag="div">
										{device === 'desktop' && <span className={css.index}>0{idx + 1}</span>}
										<span>{description}</span>
									</Typography>
								</li>
							))}
					</ul>
				</div>
			</div>
		</section>
	);
};

export default PinnedSlideshowSection;
